import AdminContainer from './AdminContainer';
import Page from './Page';
import PageWithNavbar from './PageWithNavbar';
import { useFeatures } from '@utils/hooks';
import {
  Link,
  RouteObject,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { Suspense, lazy } from 'react';
import Loading from '@components/shared/Loading';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { CampaignList } from '@features/campaign-list';
import { Stack, Typography } from '@mui/material';
import { SettingsContextValue } from '@contexts/SettingsContext';
import { CampaignLayout as V3CampaignLayout } from '@features/campaigns';
import { CampaignLayout as CpgCampaignLayout } from '@features/campaigns/subdomains/cpg';
import { CampaignLayout as RslCampaignLayout } from '@features/campaigns/subdomains/rsl';
import { BubbleRouteError } from './BubbleRouteError';

const ReportResultsContainer = lazy(
  () => import('@components/ReportResults/ReportResultsContainer')
);
const UserSettings = lazy(
  () => import('@components/userSettings/UserSettingsContainer')
);
const Stats = lazy(() => import('@components/Stats/Stats'));
const LciReportSetupFormContainer = lazy(() => import('@features/lci-report'));
const CpgLciReportSetupFormContainer = lazy(
  () => import('@features/lci-report/subdomains/cpg')
);
const RslReportSetupFormContainer = lazy(
  () => import('@features/lci-report/subdomains/rsl')
);
const EditCampaignAnalyticsReport = lazy(
  () => import('@components/CampaignAnalytics/admin')
);
const VisitationExecutiveReportContainer = lazy(
  () =>
    import('@components/ExecutiveReport/visitation/ExecutiveReportContainer')
);
const CpgExecutiveReportContainer = lazy(
  () => import('@components/ExecutiveReport/cpg/ExecutiveReportContainer')
);
const RslExecutiveReportContainer = lazy(
  () => import('@components/ExecutiveReport/rsl/ExecutiveReportContainer')
);
const Users = lazy(() => import('../components/Users/Users'));
const SsoUserSettings = lazy(
  () => import('@components/userSettings/SsoUserSettings')
);
const Usage = lazy(() => import('@components/Usage/Usage'));
const AutomatedDeliveries = lazy(
  () => import('@components/AutomatedDeliveries/AutomatedDeliveries')
);
const FlexibleReportContainer = lazy(
  () => import('@features/flexible-reports/routes')
);
const FeatureFlags = lazy(() => import('@features/feature-flags/routes'));

type RouterProps = {
  basename?: string;
  features: SettingsContextValue['features'];
};

const router = (props: RouterProps) => {
  const { features } = props;
  const isRslEnabled = features.rslCampaigns;

  const routes: RouteObject[] = [
    {
      path: '/campaigns/v3/:id/*',
      element: (
        <PageWithNavbar
          title="Campaign Details"
          footerProps={{
            sx: {
              background: unifiedPlatformColorPalette.lightBackground,
            },
          }}
        >
          <V3CampaignLayout
            basePath="/campaigns/v3"
            breadcrumbs={[{ path: '/', label: 'Media Measurement' }]}
          />
        </PageWithNavbar>
      ),
    },
    {
      path: '/campaigns/cpg/:id/*',
      element: (
        <PageWithNavbar
          title="Campaign Details"
          helpModalDefaultType="cpg"
          footerProps={{
            sx: {
              background: unifiedPlatformColorPalette.lightBackground,
            },
          }}
        >
          <CpgCampaignLayout
            basePath="/campaigns/cpg"
            breadcrumbs={[{ path: '/', label: 'Media Measurement' }]}
          />
        </PageWithNavbar>
      ),
    },
    ...(isRslEnabled
      ? ([
          {
            path: '/campaigns/rsl/:id/*',
            element: (
              <PageWithNavbar
                title="Campaign Details"
                helpModalDefaultType="rsl"
                footerProps={{
                  sx: {
                    background: unifiedPlatformColorPalette.lightBackground,
                  },
                }}
              >
                <RslCampaignLayout
                  basePath="/campaigns/rsl"
                  breadcrumbs={[{ path: '/', label: 'Media Measurement' }]}
                />
              </PageWithNavbar>
            ),
          },
        ] as RouteObject[])
      : []),
    {
      path: '/reports/:id',
      element: (
        <PageWithNavbar
          title="Report Results | CRT"
          navbarProps={{
            style: {
              position: 'sticky',
              backgroundColor: 'white',
              top: 0,
              boxShadow: '0 4px 4px -2px #ccc',
            },
          }}
        >
          <Suspense fallback={<Loading />}>
            <ReportResultsContainer />
          </Suspense>
        </PageWithNavbar>
      ),
    },
    {
      path: '/reports/redesign/:id',
      element: (
        <PageWithNavbar
          title="Report Results | CRT"
          navbarProps={{
            style: {
              position: 'sticky',
              backgroundColor: 'white',
              top: 0,
              boxShadow: '0 4px 4px -2px #ccc',
            },
          }}
        >
          <Suspense fallback={<Loading />}>
            <ReportResultsContainer redesign />
          </Suspense>
        </PageWithNavbar>
      ),
    },
    {
      path: '/campaigns/v3/:id/flexible-report-requests/:flexibleReportRequestId',
      element: (
        <PageWithNavbar
          title="Flexible Report Form"
          footerProps={{
            sx: { display: 'none' },
          }}
        >
          <Suspense fallback={<Loading />}>
            <FlexibleReportContainer />
          </Suspense>
        </PageWithNavbar>
      ),
    },
    {
      path: '/campaigns/v3/:id/flexible-reports/:flexibleReportRequestId',
      element: (
        <PageWithNavbar
          title="Flexible Report Results"
          footerProps={{
            sx: {
              background: unifiedPlatformColorPalette.lightBackground,
            },
          }}
        >
          <Suspense fallback={<Loading />}>
            <FlexibleReportContainer />
          </Suspense>
        </PageWithNavbar>
      ),
    },
    {
      path: '/',
      element: <PageWithNavbar />,
      children: [
        {
          path: '/',
          element: (
            <Page title="Campaign List">
              <CampaignList
                breadcrumbs={[{ path: '/', label: 'Media Measurement' }]}
              />
            </Page>
          ),
        },
        {
          path: '/campaigns',
          element: (
            <Page title="Campaign List">
              <CampaignList
                breadcrumbs={[{ path: '/', label: 'Media Measurement' }]}
              />
            </Page>
          ),
        },
        {
          path: '/campaigns/v3/:id/lci-report',
          element: (
            <AdminContainer allowInternal>
              <Page title="LCI Report Setup">
                <Suspense fallback={<Loading />}>
                  <LciReportSetupFormContainer />
                </Suspense>
              </Page>
            </AdminContainer>
          ),
        },
        {
          path: '/campaigns/cpg/:id/lci-report',
          element: (
            <AdminContainer allowInternal>
              <Page title="LCI Report Setup">
                <Suspense fallback={<Loading />}>
                  <CpgLciReportSetupFormContainer />
                </Suspense>
              </Page>
            </AdminContainer>
          ),
        },
        {
          path: '/campaigns/cpg/:id/executive-report',
          element: (
            <AdminContainer allowInternal>
              <Page title="Executive Report Setup">
                <Suspense fallback={<Loading />}>
                  <CpgExecutiveReportContainer />
                </Suspense>
              </Page>
            </AdminContainer>
          ),
        },
        ...(isRslEnabled
          ? ([
              {
                path: '/campaigns/rsl/:id/rsl-report',
                element: (
                  <AdminContainer allowInternal>
                    <Page title="RSL Report Setup">
                      <Suspense fallback={<Loading />}>
                        <RslReportSetupFormContainer />
                      </Suspense>
                    </Page>
                  </AdminContainer>
                ),
              },
              {
                path: '/campaigns/rsl/:id/lci-report',
                element: (
                  <AdminContainer allowInternal>
                    <Page title="LCI Report Setup">
                      <Suspense fallback={<Loading />}>
                        <RslReportSetupFormContainer />
                      </Suspense>
                    </Page>
                  </AdminContainer>
                ),
              },
              {
                path: '/campaigns/rsl/:id/executive-report',
                element: (
                  <AdminContainer allowInternal>
                    <Page title="Executive Report Setup">
                      <Suspense fallback={<Loading />}>
                        <RslExecutiveReportContainer />
                      </Suspense>
                    </Page>
                  </AdminContainer>
                ),
              },
            ] as RouteObject[])
          : []),
        {
          path: '/admin/stats',
          element: (
            <Page title="Campaign Approvals">
              <Suspense fallback={<Loading />}>
                <Stats />
              </Suspense>
            </Page>
          ),
        },
        {
          path: '/admin/campaigns/:id/campaign-analytics-reports/:reportId',
          element: (
            <AdminContainer>
              <Page title="Edit Campaign Analytics Report">
                <Suspense fallback={<Loading />}>
                  <EditCampaignAnalyticsReport />
                </Suspense>
              </Page>
            </AdminContainer>
          ),
        },
        {
          path: '/users',
          element: (
            <Page title="Users">
              <Suspense fallback={<Loading />}>
                <Users />
              </Suspense>
            </Page>
          ),
        },
        {
          path: '/users/:id/edit',
          element: (
            <Page title="User Settings">
              <Suspense fallback={<Loading />}>
                <UserSettings />
              </Suspense>
            </Page>
          ),
        },
        {
          path: '/users/new',
          element: (
            <Page title="User Settings">
              <Suspense fallback={<Loading />}>
                <UserSettings />
              </Suspense>
            </Page>
          ),
        },
        {
          path: '/sso-setup/:ssoUserId',
          element: (
            <Suspense fallback={<Loading />}>
              <SsoUserSettings />
            </Suspense>
          ),
        },
        {
          path: '/automated-deliveries',
          element: (
            <AdminContainer redirect>
              <Page title="Automated Deliveries">
                <Suspense fallback={<Loading />}>
                  <AutomatedDeliveries />
                </Suspense>
              </Page>
            </AdminContainer>
          ),
        },
        {
          path: '/campaigns/:id/executive-report',
          element: (
            <AdminContainer redirect allowInternal>
              <Page title="Executive Report Setup">
                <Suspense fallback={<Loading />}>
                  <VisitationExecutiveReportContainer />
                </Suspense>
              </Page>
            </AdminContainer>
          ),
        },
        {
          path: '/usage',
          element: (
            <AdminContainer redirect>
              <Page title="Usage">
                <Suspense fallback={<Loading />}>
                  <Usage />
                </Suspense>
              </Page>
            </AdminContainer>
          ),
        },
        {
          path: '/feature-flags',
          element: (
            <AdminContainer redirect>
              <Page title="Feature Flags">
                <Suspense fallback={<Loading />}>
                  <FeatureFlags />
                </Suspense>
              </Page>
            </AdminContainer>
          ),
        },
      ],
    },
    {
      path: '*',
      element: (
        <PageWithNavbar title="Not Found">
          <Stack direction="column" gap="1rem" padding="1rem">
            <Typography variant="h2">Not Found</Typography>
            <Typography>
              The page you are looking for does not exist.
            </Typography>
            <Stack direction="row" alignItems="center">
              <Typography>Click&nbsp;</Typography>
              <Link to="/">
                <Typography>here</Typography>
              </Link>
              <Typography>&nbsp;to go back to the home page.</Typography>
            </Stack>
          </Stack>
        </PageWithNavbar>
      ),
    },
  ];

  return createBrowserRouter(
    routes.map(route => ({
      ...route,
      errorElement: <BubbleRouteError />,
    })),
    {
      basename: props.basename,
    }
  );
};

type RoutesContainerProps = {
  basename?: string;
};

export const RoutesContainer = (props: RoutesContainerProps) => {
  const features = useFeatures();

  return (
    <RouterProvider router={router({ features, basename: props.basename })} />
  );
};
